import { useEffect, useState } from 'react';
import { AlertCircle, Box, Plus, X, XCircle } from 'lucide-react';
import { useItemRelationshipsStore } from '../../../stores/prioritization/itemRelationshipsStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { SearchableItemPicker } from '../SearchableItemPicker';

export type CompositionMode = 'add_parts' | 'make_part';

interface AddCompositionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (edgeId: string) => void;
  anchorItemId?: string;
}

export function AddCompositionModal({
  isOpen,
  onClose,
  onCreated,
  anchorItemId,
}: AddCompositionModalProps) {
  const createEdge = useItemRelationshipsStore((s) => s.createEdge);
  const fetchRelationships = useItemRelationshipsStore((s) => s.fetchFromSupabase);
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);
  const allItems = useItemsStore((s) => s.items);

  const [anchorId, setAnchorId] = useState(anchorItemId ?? '');
  const [mode, setMode] = useState<CompositionMode>('add_parts');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [pickerKey, setPickerKey] = useState(0);
  const [rationale, setRationale] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setAnchorId(anchorItemId ?? '');
    void fetchItems();
    void fetchRelationships();
  }, [isOpen, anchorItemId, fetchItems, fetchRelationships]);

  useEffect(() => {
    if (!isOpen) return;
    setMode('add_parts');
    setSelectedIds([]);
    setPickerKey((key) => key + 1);
    setRationale('');
    setError(null);
  }, [isOpen, anchorItemId]);

  const addItem = (id: string | undefined) => {
    if (!id || id === anchorId || selectedIds.includes(id)) return;
    setSelectedIds((current) => [...current, id]);
    setPickerKey((key) => key + 1);
    setError(null);
  };

  const handleCreate = async () => {
    if (!anchorId) {
      setError('This item is required');
      return;
    }
    if (selectedIds.length === 0) {
      setError(mode === 'add_parts' ? 'Add at least one part' : 'Choose a whole item');
      return;
    }

    setIsCreating(true);
    setError(null);
    let lastEdgeId: string | null = null;
    try {
      for (const selectedId of selectedIds) {
        const parentId = mode === 'add_parts' ? anchorId : selectedId;
        const childId = mode === 'add_parts' ? selectedId : anchorId;
        const edge = await createEdge({
          // Preserve the viewpoint of authorship while retaining one UI family.
          type: mode === 'add_parts' ? 'breakdown' : 'aggregation',
          directionality: 'directed',
          status: 'active',
          declaredMode: 'declared',
          sourceItemId: parentId,
          targetItemId: childId,
          rationale: rationale.trim() || undefined,
        });
        if (edge) lastEdgeId = edge.id;
      }
      if (!lastEdgeId) {
        setError('Failed to create composition link');
        return;
      }
      onCreated?.(lastEdgeId);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create composition link');
    } finally {
      setIsCreating(false);
    }
  };

  if (!isOpen) return null;

  const isMany = mode === 'add_parts';
  const selected = isMany ? selectedIds : selectedIds.slice(0, 1);
  const selectedLabel = mode === 'add_parts' ? 'Parts of this item' : 'This item is part of';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--rule)]">
          <div className="flex items-center gap-3">
            <Box className="h-6 w-6 text-[var(--green)]" />
            <div>
              <h2 className="text-xl font-semibold text-[var(--text-pri)]">Add composition</h2>
              <p className="text-sm text-[var(--text-ter)]">Connect a whole with its constituent parts</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-[var(--hover-tint)]" aria-label="Close">
            <X className="h-5 w-5 text-[var(--text-ter)]" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-[var(--coral-dim)] border border-[var(--coral)] text-[var(--coral)] text-sm">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {error}
            </div>
          )}

          <div>
            <p className="text-sm font-medium text-[var(--text-sec)] mb-2">What are you adding?</p>
            <div className="grid grid-cols-2 gap-2">
              {([
                ['add_parts', 'Parts of this item', 'This item is the whole'],
                ['make_part', 'A whole for this item', 'This item is a part'],
              ] as const).map(([value, label, description]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => { setMode(value); setSelectedIds([]); setPickerKey((key) => key + 1); }}
                  className={` border p-3 text-left transition-colors ${
                    mode === value ? 'border-[var(--green)] bg-[var(--green-dim)]' : 'border-[var(--rule)] hover:border-[var(--rule)]'
                  }`}
                >
                  <span className="block text-sm font-medium text-[var(--text-pri)]">{label}</span>
                  <span className="block text-xs text-[var(--text-ter)] mt-0.5">{description}</span>
                </button>
              ))}
            </div>
          </div>

          {!anchorItemId && (
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1.5">This item</label>
              <SearchableItemPicker
                value={anchorId || undefined}
                onChange={(id) => { setAnchorId(id ?? ''); setSelectedIds([]); }}
                excludeIds={selectedIds}
                label=""
                placeholder="Search for the anchor item…"
                lifecycleStates={[]}
                allowEmpty={false}
                allowCreate
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1.5">{selectedLabel}</label>
            {selected.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {selected.map((id) => (
                  <span key={id} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[var(--green-dim)] border border-[var(--green)] text-[var(--green)] dark:text-[var(--text-ter)] text-sm rounded-full">
                    {allItems.find((item) => item.id === id)?.title ?? id}
                    <button type="button" onClick={() => setSelectedIds((current) => current.filter((value) => value !== id))} aria-label="Remove">
                      <XCircle className="h-3.5 w-3.5" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            {(isMany || selected.length === 0) && (
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <SearchableItemPicker
                    key={pickerKey}
                    value={undefined}
                    onChange={addItem}
                    excludeIds={[anchorId, ...selectedIds].filter(Boolean)}
                    label=""
                    placeholder={mode === 'add_parts' ? 'Search to add a part…' : 'Search for the whole item…'}
                    lifecycleStates={[]}
                    allowEmpty
                    allowCreate
                  />
                </div>
                {isMany && <Plus className="h-4 w-4 text-[var(--text-ter)] shrink-0" />}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Rationale <span className="font-normal text-[var(--text-ter)]">(optional)</span></label>
            <textarea
              value={rationale}
              onChange={(event) => setRationale(event.target.value)}
              placeholder="Why are these items part of the same whole?"
              rows={2}
              className="w-full px-4 py-2 border border-[var(--rule)] resize-none"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[var(--rule)] bg-[var(--surface)]">
          <button type="button" onClick={onClose} className="px-4 py-2 text-[var(--text-sec)] font-medium hover:bg-[var(--hover-tint)]">Cancel</button>
          <button
            type="button"
            onClick={() => void handleCreate()}
            disabled={!anchorId || selected.length === 0 || isCreating}
            className="px-4 py-2 bg-[var(--green)] text-white font-medium hover:bg-[var(--green)] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isCreating ? 'Adding…' : 'Add composition'}
          </button>
        </div>
      </div>
    </div>
  );
}
